import React, {useCallback, useEffect, useMemo, useState, useTransition} from 'react';
import {Box, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import SearchInput from '../../../components/base/input/search_input';
import useAccessLevels from '../../../hooks/use-access-levels';
import {groupCrossSalesByRelation} from '../utils/group-cross-sales-by-relation';
import {filterCrossSalesBySearch} from '../utils/filter-cross-sales-by-search';
import {
    applyQuantityChange,
    applySelectionChange,
    buildStagedLinesFromState,
    createDefaultStaging,
    createEmptyStagingState,
    getStagingSummary,
} from '../utils/suggestive-selling-staging';
import {SUGGESTIVE_SELLING_VIEW_MODES} from '../constants';
import SuggestiveSellingCategoryTabs from './suggestive-selling-category-tabs';
import SuggestiveSellingProductGrid from './suggestive-selling-product-grid';
import SuggestiveSellingDataGrid from './suggestive-selling-data-grid';
import SuggestiveSellingViewModeToggle from './suggestive-selling-view-mode-toggle';

const SuggestiveSellingDialogContent = ({
                                            crossSales,
                                            sourceDefaultQuantity,
                                            disabled,
                                            onStagingChange,
                                        }) => {
    const {t} = useTranslation('dialogs', {keyPrefix: 'app-specific.suggestive-selling'});
    const operatorAccessLevels = useAccessLevels();
    const [staging, setStaging] = useState(createEmptyStagingState);
    const [activeGroupId, setActiveGroupId] = useState(null);
    const [viewMode, setViewMode] = useState(SUGGESTIVE_SELLING_VIEW_MODES.grid);
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearchPending, startSearchTransition] = useTransition();

    const handleSearch = useCallback((value) => {
        startSearchTransition(() => {
            setSearchQuery(value ?? '');
        });
    }, [startSearchTransition]);

    const {hasCategories, groups} = useMemo(
        () => groupCrossSalesByRelation(crossSales),
        [crossSales],
    );

    const activeGroup = useMemo(() => {
        if (!groups.length) {
            return null;
        }
        const found = groups.find((g) => g.id === activeGroupId);
        return found ?? groups[0];
    }, [activeGroupId, groups]);

    const activeParts = useMemo(() => activeGroup?.parts ?? [], [activeGroup?.parts]);

    const isSearching = Boolean(searchQuery.trim());

    const displayParts = useMemo(() => {
        const sourceParts = isSearching ? crossSales : activeParts;
        return filterCrossSalesBySearch(sourceParts, searchQuery, {
            includePrices: operatorAccessLevels.packages.sales.pricesInLookup,
        });
    }, [
        activeParts,
        crossSales,
        isSearching,
        operatorAccessLevels.packages.sales.pricesInLookup,
        searchQuery,
    ]);

    useEffect(() => {
        setSearchQuery('');
        setActiveGroupId(groups[0]?.id ?? null);
        if (!crossSales.length) {
            setStaging(createEmptyStagingState());
            return;
        }
        setStaging(createDefaultStaging(crossSales, sourceDefaultQuantity));
    }, [crossSales, groups, sourceDefaultQuantity]);

    const stagingSummary = useMemo(() => getStagingSummary(staging), [staging]);

    useEffect(() => {
        onStagingChange?.(staging);
    }, [onStagingChange, staging]);

    const handleQuantityChange = useCallback((partId, quantity) => {
        setStaging((prev) => applyQuantityChange(prev, partId, quantity, {
            allowZero: operatorAccessLevels.packages.sales.issueCredit,
        }));
    }, [operatorAccessLevels.packages.sales.issueCredit]);

    const handleSelectionChange = useCallback((partId, selected) => {
        setStaging((prev) => applySelectionChange(prev, partId, selected));
    }, []);

    return (
        <Box>
            <SearchInput
                autoSearch
                immediateSearch
                fullWidth
                variant={'outlined'}
                label={t('searchLabel')}
                placeholder={t('searchPlaceholder')}
                initialValue={searchQuery}
                disabled={disabled || !crossSales.length}
                search={handleSearch}
                paperProps={{className: 'px-0', sx: {mb: 1.5}}}
            />
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 1,
                    mb: 1.5,
                    flexWrap: 'wrap',
                }}
            >
                {
                    stagingSummary.lineCount > 0
                        ? (
                            <Typography variant={'body2'} color={'text.secondary'}>
                                {t('selectedSummary', {
                                    count: stagingSummary.lineCount,
                                    quantity: stagingSummary.totalQty,
                                })}
                            </Typography>
                        )
                        : <span/>
                }
                <SuggestiveSellingViewModeToggle
                    viewMode={viewMode}
                    onChange={setViewMode}
                    disabled={disabled}
                />
            </Box>
            {
                hasCategories && groups.length > 1 && !isSearching &&
                <SuggestiveSellingCategoryTabs
                    groups={groups}
                    activeGroupId={activeGroup?.id ?? groups[0]?.id}
                    onChange={setActiveGroupId}
                />
            }
            <Box
                sx={{
                    opacity: isSearchPending ? 0.65 : 1,
                    transition: 'opacity 0.15s ease-in-out',
                    pointerEvents: isSearchPending ? 'none' : 'auto',
                }}
                aria-busy={isSearchPending}
            >
                {
                    crossSales.length > 0 && displayParts.length === 0 && !isSearchPending
                        ? (
                            <Typography variant={'body2'} color={'text.secondary'} sx={{py: 2}}>
                                {t('noSearchResults')}
                            </Typography>
                        )
                        : (
                            viewMode === SUGGESTIVE_SELLING_VIEW_MODES.grid
                                ? (
                                    <SuggestiveSellingProductGrid
                                        parts={displayParts}
                                        staging={staging}
                                        onQuantityChange={handleQuantityChange}
                                        onSelectionChange={handleSelectionChange}
                                        disabled={disabled}
                                    />
                                )
                                : (
                                    <SuggestiveSellingDataGrid
                                        parts={displayParts}
                                        staging={staging}
                                        onQuantityChange={handleQuantityChange}
                                        onSelectionChange={handleSelectionChange}
                                        disabled={disabled}
                                    />
                                )
                        )
                }
            </Box>
        </Box>
    );
};

export default SuggestiveSellingDialogContent;

export {buildStagedLinesFromState};
